import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Drink } from "src/lib/db";
import { DrinkCacheRecord, cacheDrink } from "src/lib/slices/drinkCache";
import { TabsContent } from "../ui/tabs";
import DrinkCard from "./DrinkCard";
import DrinkLoadingCard from "./DrinkLoadingCard";

export default function DrinkTabContent({ drinkType, selection, selectDrink }) {
  const dispatch = useDispatch();

  const [apiLoaded, setApiLoaded] = useState([]);
  const [apiLoadedAll, setApiLoadedAll] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);

  const [forceLoad, setForceLoad] = useState(false);
  const failedAttempts = useRef(0);

  const [loadingElementVisible, setLoadingElementVisible] = useState(false);

  const ownDrinks: Drink[] = useSelector(
    (state: any) => state.ownItems.Drinks
  ).filter((drink) => drink.type === drinkType.key);

  const drinkCache: DrinkCacheRecord[] = useSelector(
    (state: any) => state.drinkCache.drinks
  ).filter((record) => record.drink.type === drinkType.key);

  useEffect(() => {
    if (
      apiLoadedAll ||
      apiLoading ||
      !loadingElementVisible ||
      (failedAttempts.current >= 3 && !forceLoad)
    )
      return;

    setApiLoading(true);
    if (forceLoad) setForceLoad(false);

    (async () => {
      const response = await axios
        .post<{ drinks: Drink[]; all: boolean }>(
          `${process.env.REACT_APP_API_URL}/drinks`,
          {
            loaded: [
              ...new Set([
                ...apiLoaded.map((drink) => drink.id),
                ...drinkCache.map((record) => record.drink.id),
              ]),
            ],
            type: drinkType.key,
          }
        )
        .catch(() => {});

      setApiLoading(false);

      if (!response) {
        failedAttempts.current++;
        return;
      }

      failedAttempts.current = 0;

      setApiLoaded((currentState) => [
        ...currentState,
        ...response.data.drinks,
      ]);
      setApiLoadedAll(response.data.all);
    })();
  }, [
    apiLoading,
    apiLoaded,
    apiLoadedAll,
    loadingElementVisible,
    drinkType.key,
    forceLoad,
    drinkCache,
  ]);

  const retryAvailable = failedAttempts.current >= 3 && !apiLoading;

  const allDrinks = useMemo(() => {
    let sortedDrinkCache = [...drinkCache];
    sortedDrinkCache.sort(
      (a, b) => (a.lastUsedAt ?? Infinity) - (b.lastUsedAt ?? Infinity)
    );
    const sortedCachedDrinks = sortedDrinkCache.map((record) => record.drink);
    let drinks = [...ownDrinks, ...sortedCachedDrinks];
    drinks = [
      ...drinks,
      ...apiLoaded.filter(
        (drink) => !sortedCachedDrinks.find((d) => d.id === drink.id)
      ),
    ];
    return drinks;
  }, [apiLoaded, drinkCache, ownDrinks]);

  const cacheDrinkAndSelect = (drinkId) => {
    if (!apiLoaded.find((drink) => drink.id === drinkId)) {
      return selectDrink(drinkId);
    }
    const drink = apiLoaded.find((drink) => drink.id === drinkId);
    dispatch(cacheDrink({ drink }));

    selectDrink(drinkId, drink);
  };

  return (
    <TabsContent
      value={drinkType.key}
      key={drinkType.key}
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(8rem, 1fr))",
        gridTemplateRows: "repeat(auto-fill, 1fr)",
        gap: "1rem",
      }}
      className="mt-4"
    >
      {allDrinks.map((drink) => (
        <DrinkCard
          drink={drink}
          selection={selection}
          selectDrink={cacheDrinkAndSelect}
          key={drink.id}
        />
      ))}
      {!apiLoadedAll && (
        <DrinkLoadingCard
          retryAvailable={retryAvailable}
          setVisible={setLoadingElementVisible}
          setForceLoad={setForceLoad}
        />
      )}
    </TabsContent>
  );
}
